<script>
  import { md } from '@st-graphics/graphics-kit/utils'
  import Scroller from '@sveltejs/svelte-scroller'

  import { sheetJson } from '$state/data'
  import { fade } from 'svelte/transition'

  import WordCloudCanvas from '../WordCloud/WordCloudCanvas.svelte'

  let {
    value,
    cardsalign = 'center', // left, center, right
    top = 0,
    bottom = 0,
    threshold = 1,
    count = $bindable(0),
    index = $bindable(0),
    offset = $bindable(0),
    progress = $bindable(0),
  } = $props()

  let cloudWidth = $state(0)
  let cloudHeight = $state(0)
  const cards = $derived(value.cards || [])

  const getYearBin = (yearStr) => {
    const year = parseInt(yearStr, 10)
    if (isNaN(year)) return null

    switch (true) {
      case year <= 2004:
        return '2000–2004'
      case year <= 2009:
        return '2005–2009'
      case year <= 2014:
        return '2010–2014'
      case year <= 2019:
        return '2015–2019'
      case year <= 2024:
        return '2020–2024'
      case year <= 2029:
        return '2025–2029'
      default:
        return '2030–2031'
    }
  }

  // Aggregate word frequencies from sheetJson.data
  const wordCloudData = $derived.by(() => {
    if (!sheetJson || !sheetJson.data) return []

    const wordMap = new Map()
    sheetJson.data.forEach((item) => {
      const word = String(item['descriptive-word'] || '').trim()
      const category = String(item['category'] || '').trim()
      const yearBin = getYearBin(item['year-completed'])

      if (word && word !== '-' && category && yearBin) {
        if (!wordMap.has(word)) {
          wordMap.set(word, { size: 0, category, yearBins: new Set() })
        }
        wordMap.get(word).size += 1
        wordMap.get(word).yearBins.add(yearBin)
      }
    })

    return Array.from(wordMap.entries())
      .map(([text, { size, category, yearBins }]) => ({
        text,
        size,
        category,
        yearBins: Array.from(yearBins),
      }))
      .sort((a, b) => b.size - a.size)
      .map((item, i) => ({
        ...item,
        // top 3 words get orange, rest use component fallback
        // might be better to change later to highlights props as array or so
        color: i < 3 ? 'var(--project-highlight-color, #cfff5e)' : undefined,
      }))
  })

  const getActiveCategory = (idx) => {
    switch (idx) {
      case 1:
        return 'Greenery & Gardens'
      case 2:
        return 'Water & Breezes'
      case 3:
        return 'Feeling'
      default:
        return null
    }
  }

  const getActiveWords = (idx) => {
    switch (idx) {
      case 4:
        return ['Arcadia']
      case 5:
        return ['Apex', 'Heights', 'Vantage', 'Summit']
      case 6:
        return ['Oleander', 'Miltonia', 'Spring', 'Bloom']
      default:
        return idx >= 4 ? [] : null
    }
  }

  const getActiveWordColors = (idx) => {
    switch (idx) {
      case 5:
        return {
          Heights: '#ffffff',
        }
      case 6:
        return {
          Bloom: '#ffffff',
          Spring: '#ffffff',
          Oleander: 'var(--project-highlight-color, #cfff5e)',
          Miltonia: 'var(--project-highlight-color, #cfff5e)',
        }
      default:
        return null
    }
  }

  const getMode = (idx, off) => {
    if (idx >= 4) return 'years'
    if (idx >= 1) return 'groups'
    //if ((idx === 0 && off > 0.5) || idx >= 1) return 'groups'
    return 'cloud'
  }
</script>

<Scroller
  {top}
  {bottom}
  {threshold}
  bind:count
  bind:index
  bind:offset
  bind:progress
  query=".scrolly-card"
>
  {#snippet background()}
    <div class="scrolly-background">
      <div class="sr-only" aria-live="assertive">
        Scrolly background is updated for slide {index + 1}. {cards[index]?.bgalt || ''}
      </div>

      <!--<div
        class="debug"
        style="position:absolute; top:0; left:0; z-index:10; font-size: 14px; z-index: 999;"
      >
        card:{index} | {offset.toFixed(2)}<br />
      </div>-->

      <div
        style="width: 100%; height: 100vh; position: relative;"
        bind:clientWidth={cloudWidth}
        bind:clientHeight={cloudHeight}
      >
        {#if cloudWidth > 0 && cloudHeight > 0}
          <div style="position: absolute; inset: 0;" transition:fade={{ duration: 600 }}>
            <WordCloudCanvas
              words={wordCloudData}
              width={cloudWidth}
              height={cloudHeight}
              mode={getMode(index, offset)}
              activeCategory={getActiveCategory(index)}
              activeWords={getActiveWords(index)}
              activeWordColors={getActiveWordColors(index)}
              {offset}
              {index}
            />
          </div>
        {/if}
      </div>
    </div>
  {/snippet}

  {#snippet foreground()}
    <div class="scrolly-foreground">
      <div class="cards-container {cardsalign}">
        {#each cards as { text, thumbnail, alt, classname = undefined, lazy = false }, i (i)}
          <div
            class="scrolly-section scrolly-card {classname}"
            class:blank={text.length < 1}
            role={text.length >= 1 ? 'group' : undefined}
            aria-label={text.length >= 1 ? `Slide ${i + 1}` : undefined}
          >
            <p class="scrolly-text">{@html md(text)}</p>
          </div>
        {/each}
      </div>
    </div>
  {/snippet}
</Scroller>

<style lang="scss">
  .scrolly-background {
    position: relative;
    height: 100vh;
    min-height: 100vh;
    contain: layout paint;
    overflow: hidden;
    will-change: transform;
  }
  .scrolly-background.no-overflow {
    overflow: visible;
  }
  .scrolly-foreground {
    display: flex;
    flex-direction: column;
    align-items: var(--scrolly-foreground-align-items, center);
    padding: 0 1rem;
    margin: 0 auto;
    transform: translateZ(0);
    pointer-events: none;
  }
  .scrolly-foreground .header-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    pointer-events: auto;
  }
  .scrolly-foreground .header-card.start {
    margin-top: -100vh;
  }
  .scrolly-foreground .cards-container {
    display: flex;
    flex-direction: column;
    align-items: var(--md-scrolly-foreground-align-items, start);
    width: 100%;
    max-width: 75rem;
    padding-bottom: max(50rem, 100vh);
    margin: 0 auto;
    pointer-events: none;
  }
  .scrolly-foreground .cards-container.left {
    align-items: flex-start;
  }
  .scrolly-foreground .cards-container.center {
    align-items: center;
  }
  .scrolly-foreground .cards-container.right {
    align-items: flex-end;
  }
  .scrolly-foreground .cards-container .scrolly-section {
    margin-bottom: max(50rem, 100vh);
  }
  .scrolly-foreground .cards-container .scrolly-card {
    width: 100%;
    max-width: var(--scrolly-card-max-width, 25rem);
    line-height: var(--scrolly-card-line-height, 1.9375rem);
    text-align: var(--scrolly-card-text-align, unset);
    pointer-events: auto;
  }
  .scrolly-foreground .cards-container .scrolly-card .scrolly-text {
    padding: var(--scrolly-text-padding, 0.625rem 0.9375rem 1.25rem);
  }
  .scrolly-foreground .cards-container .scrolly-card .scrolly-text:only-child {
    padding-top: 0.9375rem;
  }
  .scrolly-foreground .cards-container .scrolly-card:last-child {
    margin-bottom: 0;
  }
  @media (width >= 744px) {
    .scrolly-foreground .cards-container .scrolly-card {
      margin: 0 0.625rem max(50rem, 100vh);
    }
  }

  .scrolly-background {
    background: var(
      --scrolly-background-background,
      var(--theme-scrolly-background-background, transparent)
    );
  }
  .scrolly-foreground .cards-container .scrolly-card {
    font-size: var(--scrolly-card-font-size, 1.25rem);
    color: var(--scrolly-card-color, var(--theme-scrolly-card-color, var(--st-black)));
    background: var(
      --scrolly-card-background,
      var(--theme-scrolly-card-background, var(--st-white))
    );
  }
  @media (width >= 744px) {
    .scrolly-foreground .cards-container .scrolly-card {
      font-size: 1.375rem;
    }
  }
  .scrolly-foreground .cards-container .scrolly-card.blank {
    opacity: 0;
  }

  :global(svelte-scroller-background-container) {
    position: sticky !important;
    transform: translate(0, 0) !important;
    pointer-events: auto !important;
  }

  :global([data-theme='dark']) .scrolly-card {
    //--theme-scrolly-card-color: var(--st-white-primary);
    //--theme-scrolly-card-background: rgba(41, 41, 58, 0.25);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
</style>
